import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {GetAddonDataRequestParamsType, GetAddonDataResponseType} from "@CheckOrder/api/types/GetAddonDataQuery.type";
import {
    SetActiveAddonRequestParamsType,
    SetActiveAddonResponseType
} from "@CheckOrder/api/types/SetActiveAddonQuery.type";
import {
    RemoveActiveAddonRequestType,
    RemoveActiveAddonResponseType
} from "@CheckOrder/api/types/RemoveActiveAddonQuery.type";

import {UpdateAmenitiesRequestType, UpdateAmenitiesResponseType} from "@CheckOrder/api/types/UpdateAmenitiesQuery.type";
import {ApplyVoucherRequestType, ApplyVoucherResponseType} from "@CheckOrder/api/types/vouchers/ApplyVoucher.type";
import {RemoveVoucherRequestType, RemoveVoucherResponseType} from "@CheckOrder/api/types/vouchers/RemoveVoucher.type";
import applyVoucherQuery from "@CheckOrder/api/queries/applyVoucherQuery";
import removeVoucherQuery from "@CheckOrder/api/queries/removeVoucherQuery";
import setAddonQuery from "@CheckOrder/api/queries/setAddonQuery";
import removeAddonQuery from "@CheckOrder/api/queries/removeAddonQuery";
import updateAmenitiesQuery from "@CheckOrder/api/queries/updateAmenitiesQuery";
import {
    UpdatePassengerGroupRequestType,
    UpdatePassengerGroupResponseType
} from "@CheckOrder/api/types/UpdatePassengerGroup.type";
import updatePassengerGroupQuery from "@CheckOrder/api/queries/updatePassengerGroupQuery";
import getGetCheckOrderDataUrl from "@CheckOrder/api/functions/getGetCheckOrderDataUrl";
import {
    GetCheckOrderDataRequestType,
    GetCheckOrderDataResponseType
} from "@CheckOrder/api/types/GetCheckOrderDataQuery.type";
import getCheckOrderDataQuery from "@CheckOrder/api/queries/getCheckOrderDataQuery";
import {
    HandleStripePaymentRequestType,
    HandleStripePaymentResponseType
} from "@CheckOrder/api/types/HandleStripePaymentQuery.type";
import handleStripePaymentQuery from "@CheckOrder/api/queries/handleStripePaymentQuery";
import {UpdatePassengerRequestType, UpdatePassengerResponseType} from "@CheckOrder/api/types/UpdatePassengerQuery.type";
import qs from "qs";
import updatePassengerQuery from "@CheckOrder/api/queries/updatePassengerQuery";
import {GetSurveyRequestType, GetSurveyResponseType} from "@CheckOrder/api/types/GetSurveyQuery.type";
import {
    UpdateSeatSelectionResponseType,
    UpdateSeatSelectionRequestType
} from "@CheckOrder/api/types/UpdateSeatSelectionQuery.type";
import {
    UpdatePassengerClassesRequestType,
    UpdatePassengerClassesResponseType
} from "@CheckOrder/api/types/UpdatePassengerClassesQuery.type";
import updatePassengerClassesQuery from "@CheckOrder/api/queries/updatePassengerClassesQuery";

// Define a service using a base URL and expected endpoints
export const checkOrderApi = createApi({
    reducerPath: 'checkOrderApi',

    baseQuery: fetchBaseQuery({
        // baseUrl: `${GBB_URL}/api/mobile/v3/`
        headers: {
            'Content-Type': 'application/json',
            "X-Requested-With": "XMLHttpRequest",
            referer: "https://getbybus.com",
        },
    }),

    tagTypes: ['ActiveTicket', 'ActiveVoucher'],

    endpoints: (builder) => ({
        getCheckOrderData: builder.query<GetCheckOrderDataResponseType, GetCheckOrderDataRequestType>({
            query: () => ({
                url: getGetCheckOrderDataUrl(),
                method: 'GET',
            }),
            onQueryStarted: getCheckOrderDataQuery(),
        }),
        updatePassenger: builder.query<UpdatePassengerResponseType, UpdatePassengerRequestType>({
            query: (body) => ({
                url: '/ajax/ticket-update-passenger',
                method: 'POST',
                body: qs.stringify(body),
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    "X-Requested-With": "XMLHttpRequest",
                    referer: "https://getbybus.com",
                },
            }),
            onQueryStarted: updatePassengerQuery(),
        }),
        updatePassengerGroup: builder.query<UpdatePassengerGroupResponseType, UpdatePassengerGroupRequestType>({
            query: ({passenger}) => ({
                url: '/ajax/ticket-update-passenger-group',
                method: 'POST',
                body: qs.stringify(passenger),
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    "X-Requested-With": "XMLHttpRequest",
                    referer: "https://getbybus.com",
                },
            }),
            onQueryStarted: updatePassengerGroupQuery(),
        }),
        applyVoucher: builder.query<ApplyVoucherResponseType, ApplyVoucherRequestType>({
            query: ({ticket, ...body}) => ({
                url: '/ajax/apply-voucher',
                method: 'POST',
                body,
            }),
            onQueryStarted: applyVoucherQuery(),
        }),
        removeVoucher: builder.query<RemoveVoucherResponseType, RemoveVoucherRequestType>({
            query: ({ticket, ...body}) => ({
                url: '/ajax/remove-voucher',
                method: 'POST',
                body,
            }),
            onQueryStarted: removeVoucherQuery(),
        }),
        getAddon: builder.query<GetAddonDataResponseType, GetAddonDataRequestParamsType>({
            query: (params) => ({
                url: '/ajax/get-addon-data',
                method: 'GET',
                params,
            }),
        }),
        setAddon: builder.query<SetActiveAddonResponseType, SetActiveAddonRequestParamsType>({
            query: (body) => ({
                url: '/ajax/set-active-addon',
                method: 'POST',
                body,
            }),
            onQueryStarted: setAddonQuery(),
        }),
        removeAddon: builder.query<RemoveActiveAddonResponseType, RemoveActiveAddonRequestType>({
            query: (body) => ({
                url: '/ajax/remove-active-addon',
                method: 'POST',
                body,
            }),
            onQueryStarted: removeAddonQuery(),
        }),
        updateAmenities: builder.query<UpdateAmenitiesResponseType, UpdateAmenitiesRequestType>({
            query: (body) => ({
                url: '/ajax/update-amenities',
                method: 'POST',
                body,
            }),
            onQueryStarted: updateAmenitiesQuery(),
        }),
        updateSeatSelection: builder.query<UpdateSeatSelectionResponseType, UpdateSeatSelectionRequestType>({
            query: (body) => ({
                url: '/ajax/update-seat-selection',
                method: 'POST',
                body,
            }),
        }),
        updatePassengerClasses: builder.query<UpdatePassengerClassesResponseType, UpdatePassengerClassesRequestType>({
            query: (body) => ({
                url: '/ajax/update-passenger-classes',
                method: 'POST',
                body,
            }),
            onQueryStarted: updatePassengerClassesQuery(),
        }),
        handleStripePayment: builder.query<HandleStripePaymentResponseType, HandleStripePaymentRequestType>({
            query: (body) => ({
                url: '/ajax/check-order-submit',
                method: 'POST',
                body: JSON.stringify(body),
            }),
            onQueryStarted: handleStripePaymentQuery(),
        }),
        getSurvey: builder.query<GetSurveyResponseType, GetSurveyRequestType>({
            query: (params) => ({
                url: '/ajax/survey',
                method: 'GET',
                params,
            }),
        }),
    }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useLazyGetCheckOrderDataQuery,
    useLazyUpdatePassengerQuery,
    useLazyUpdatePassengerGroupQuery,
    useLazyApplyVoucherQuery,
    useLazyRemoveVoucherQuery,
    useLazySetAddonQuery,
    useLazyGetAddonQuery,
    useLazyRemoveAddonQuery,
    useLazyUpdateAmenitiesQuery,
    useLazyUpdateSeatSelectionQuery,
    useLazyUpdatePassengerClassesQuery,
    useLazyHandleStripePaymentQuery,
    useGetSurveyQuery,
} = checkOrderApi